import React, { useEffect, useState } from "react";
import { X, Thermometer, StickyNote } from "lucide-react";

/**
 * Move an appraisal between Cold / Warm / Hot, with the reason written down.
 *
 * Simon Abbott, Monday 12985995388, 7 Sep 2026: "We need to be able to change a
 * warm lead to hot or cold in list view. Add a button to change it so it moves
 * to hot or cold list and a note box to add a note as to why you changed it."
 *
 * Dragging a card between columns on the board could always move an appraisal,
 * but the list view had no way to do it at all, and neither path recorded WHY.
 * The note is required here for that reason: a temperature that changed with no
 * explanation is what the next agent to open the appraisal cannot work with. It
 * saves as a normal PropertyNote, so it shows on the row as the latest note and
 * in the property's Notes tab.
 *
 * Deliberately does NOT close on a background click (see LeadNotesModal — agents
 * kept losing typed notes that way), and Escape is not wired up.
 */

// Exported so the Edit Appraisal modal offers exactly the same three choices,
// with the same colours and the same wording, rather than a second copy that can
// drift (Christian Gravias, Monday 13057749471, 17 Sep 2026).
export const TEMPERATURES = [
    {
        value: "cold",
        label: "Cold",
        hint: "Not selling for a while",
        active: "border-[#50C8FF] bg-[#DCF4FF] text-[#0284C7]",
        idle: "border-[#E4E6E9] bg-white text-[#6B7280] hover:border-[#50C8FF]",
    },
    {
        value: "warm",
        label: "Warm",
        hint: "Thinking about it",
        active: "border-[#F89542] bg-[#FFEEE1] text-[#EA7B1F]",
        idle: "border-[#E4E6E9] bg-white text-[#6B7280] hover:border-[#F89542]",
    },
    {
        value: "hot",
        label: "Hot",
        hint: "Ready to list",
        active: "border-[#4BCF0E] bg-[#EBFFF2] text-[#15A34A]",
        idle: "border-[#E4E6E9] bg-white text-[#6B7280] hover:border-[#4BCF0E]",
    },
];

export const labelFor = (value) =>
    TEMPERATURES.find((t) => t.value === String(value || "").toLowerCase())?.label || "Unassigned";

const AppraisalTemperatureModal = ({ isOpen, property, onClose, onSave, saving = false }) => {
    const current = String(property?.appraisal_temperature || "").toLowerCase();
    const [target, setTarget] = useState("");
    const [note, setNote] = useState("");
    const [error, setError] = useState("");

    // Reset every time a different appraisal is opened, so neither the chosen
    // temperature nor the typed reason can carry over to the next row.
    useEffect(() => {
        setTarget("");
        setNote("");
        setError("");
    }, [property?.id]);

    if (!isOpen || !property) return null;

    const address =
        property.address ||
        [property.street_name, property.suburb, property.state].filter(Boolean).join(", ") ||
        property.title ||
        "this appraisal";

    const handleSave = () => {
        if (!target) {
            setError("Pick where this appraisal is moving to.");
            return;
        }
        if (!note.trim()) {
            setError("Add a note saying why it moved.");
            return;
        }
        // The note carries the move itself, so the Notes tab reads as a history
        // rather than a list of unexplained comments.
        const composed = `Moved from ${labelFor(current)} to ${labelFor(target)}. ${note.trim()}`;
        onSave(property, target, composed);
    };

    return (
        // No onClick on this backdrop on purpose: only the X and Cancel close it.
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in font-sans text-[#353B48]">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
                <div
                    className="px-6 py-5 bg-[#F7F9FB] flex items-start justify-between gap-3"
                    style={{ borderBottom: "0.45px solid #D3D3D3" }}
                >
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
                            <Thermometer className="w-5 h-5 text-[#55C5D0]" />
                            Move Appraisal
                        </h2>
                        <p className="text-[12px] text-[#6B7280] font-medium truncate mt-0.5">
                            {address} · currently {labelFor(current)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#9CA3AF] shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
                    <div className="space-y-2">
                        <p className="text-[13px] font-bold text-[#353B48]">Move to</p>
                        <div className="grid grid-cols-3 gap-2">
                            {TEMPERATURES.map((t) => {
                                const isCurrent = t.value === current;
                                const isSelected = t.value === target;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        disabled={isCurrent}
                                        aria-pressed={isSelected}
                                        onClick={() => {
                                            setTarget(t.value);
                                            setError("");
                                        }}
                                        className={`rounded-xl border-2 px-3 py-3 text-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                                            isSelected ? t.active : t.idle
                                        }`}
                                    >
                                        <span className="block text-[14px] font-bold">{t.label}</span>
                                        <span className="block text-[10px] font-medium opacity-80 mt-0.5">
                                            {isCurrent ? "Current" : t.hint}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="temperature-note"
                            className="text-[13px] font-bold text-[#353B48] flex items-center gap-1.5"
                        >
                            <StickyNote className="w-4 h-4 text-amber-500" />
                            Why are you moving it? <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="temperature-note"
                            rows={4}
                            value={note}
                            onChange={(e) => {
                                setNote(e.target.value);
                                setError("");
                            }}
                            placeholder="e.g. Owner has decided to list before Christmas."
                            className="w-full bg-white border border-[#E4E6E9] rounded-lg px-4 py-3 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 transition-all placeholder:text-[#9CA3AF]"
                        />
                        <p className="text-[11px] text-[#9CA3AF]">
                            Saved on the appraisal as its latest note, with the move recorded in front of it.
                        </p>
                    </div>

                    {error && <p className="text-[12px] font-bold text-red-500">{error}</p>}
                </div>

                <div className="px-6 py-5 border-t border-[#F3F4F6] flex items-center justify-end gap-3 bg-[#FDFDFD]">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="px-6 py-3 border border-[#E4E6E9] text-[#6B7280] font-bold rounded-xl hover:bg-gray-50 transition-all text-[14px] disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="px-8 py-3 bg-[#55C5D0] hover:bg-[#45b5c0] text-white font-bold rounded-xl shadow-lg shadow-[#55C5D0]/20 transition-all text-[14px] disabled:opacity-60"
                    >
                        {saving ? "Moving..." : "Move Appraisal"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AppraisalTemperatureModal;
